/**
 * Labstep
 *
 * @module services/query-parameter
 * @desc Helper methods for post filter and query parameters
 */

import type { ParamsHOCChildrenProps } from 'labstep-web/hoc/Params/types';
import {
  type EntityViewParameters,
  PostFilterComparison,
  type PostFilterNodeType,
} from 'labstep-web/services/postFilter/constants';
import pickBy from 'lodash/pickBy';

export class QueryParameterService {
  /**
   * Returns a predicate value from a post filter in parameters
   * @param parameters: EntityViewParameters
   * @param path: string
   * @returns string | number | boolean | null
   */
  static getFilterNodeValue(
    parameters: EntityViewParameters,
    path: string,
  ): string | number | boolean | null {
    if (
      !parameters ||
      !parameters.filter ||
      !parameters.filter.length ||
      !parameters.filter[0]
    ) {
      return null;
    }

    let value = null;
    if (parameters.filter[0].predicates) {
      parameters.filter[0].predicates.forEach((node) => {
        if (node.predicates && node.path === path) {
          node.predicates.forEach((predicate) => {
            if (
              QueryParameterService.isPostFilterNodeType(predicate)
            ) {
              value = predicate.value;
            }
          });
        }
      });
    }

    return value;
  }

  /**
   * Checks if a value is valid
   * @param value unknown
   * @returns boolean
   */
  static valueIsValid(value: unknown): boolean {
    return value !== undefined && value !== '' && value !== null;
  }

  /**
   * We clear empty values i.e. when removing a value from a search select
   * We keep filters that don't have a value by default
   * i.e. have a comparison of null or not_null
   * @param predicate PostFilterNodeType
   * @returns boolean
   */
  static predicateIsValid(predicate: PostFilterNodeType): boolean {
    if ('predicates' in predicate) {
      return true;
    }

    if (
      predicate.comparison !== PostFilterComparison.null &&
      predicate.comparison !== PostFilterComparison.not_null &&
      predicate.comparison !== PostFilterComparison.exists &&
      predicate.comparison !== PostFilterComparison.not_exists
    ) {
      const { value } = predicate;
      return value !== undefined && value !== '' && value !== null;
    }
    return true;
  }

  /**
   * Sanitizes a node by removing predicates that are invalid
   * And removing the node if it has no predicates
   * @param node PostFilterNodeType
   * @returns PostFilterNodeType | null
   */
  static sanitizeNode(
    node: PostFilterNodeType,
  ): PostFilterNodeType | null {
    const newPostFilter = { ...node };
    const predicates: PostFilterNodeType[] = [];
    if (node.predicates) {
      node.predicates.forEach((p) => {
        if ('predicates' in p) {
          const sanitized = QueryParameterService.sanitizeNode(p);
          if (sanitized) {
            predicates.push(sanitized);
          }
        } else if (QueryParameterService.predicateIsValid(p)) {
          predicates.push(p);
        }
      });
    }
    if (!predicates.length) {
      return null;
    }
    newPostFilter.predicates = predicates;
    return newPostFilter;
  }

  /**
   * Sanitizes filter by removing nodes that are invalid
   * And removing the filter if it has no nodes
   * @param filter Filter
   * @returns Filter
   */
  static sanitizeFilter(
    filter: EntityViewParameters['filter'],
  ): EntityViewParameters['filter'] | undefined {
    if (!filter) {
      return undefined;
    }

    const sanitizedFilter = filter.map((f) => {
      const { type, predicates } = f;
      const sanitizedNodes = predicates
        ? predicates.map(QueryParameterService.sanitizeNode)
        : [];
      const sanitizedNodesWithoutNull = sanitizedNodes.filter(
        (node) => node !== null,
      );
      return { type, predicates: sanitizedNodesWithoutNull };
    });

    if (
      sanitizedFilter.every(
        (f) => !f.predicates || f.predicates.length === 0,
      )
    ) {
      return undefined;
    }

    return sanitizedFilter as EntityViewParameters['filter'];
  }

  /**
   * Sanitizes parameters without a value
   * @param params Parameters
   * @returns Parameters
   */
  static sanitizeParams(
    params: ParamsHOCChildrenProps['searchParams'] | undefined,
  ): ParamsHOCChildrenProps['searchParams'] {
    let sanitizedParams = { ...params };
    sanitizedParams.filter = QueryParameterService.sanitizeFilter(
      sanitizedParams.filter as any,
    );
    sanitizedParams = pickBy(
      sanitizedParams || {},
      QueryParameterService.valueIsValid,
    );

    return sanitizedParams;
  }

  static isPostFilterNodeType(
    predicate: PostFilterNodeType,
  ): predicate is PostFilterNodeType {
    return 'value' in predicate;
  }
}
